// React
import { ReactElement, useState, useEffect } from 'react';
// Next
import { GetServerSideProps, NextPage } from 'next';
import { i18n } from 'next-i18next.config';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
// Modules
import { ACCESS_TOKEN_COOKIE_NAME } from '@utmb/features/sso/keycloak';
// Utils
import { fetchTheAPI } from '@utmb/utils/utmb';
import isMobile from '@utmb/utils/is-mobile';
// Features
import { CreateYourAccount } from '@utmb/features/create-your-account/CreateYourAccount';
import { Layout } from '@utmb/layouts/layout-base';
import RaceHeader from '@utmb/features/race-ranking/race-header/RaceHeader';
import RaceTable from '@utmb/features/race-ranking/race-table/RaceTable';
// Contexts
import { PageProviders } from '@utmb/contexts/index';
import { useGlobal } from '@utmb/contexts/GlobalContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { useUser } from '@utmb/contexts/AuthContext';
// Types
import RacePageView from '@utmb/types/view/RacePageView';
import RaceResultPaginatedView from '@utmb/types/view/RaceResultPaginatedView';
import { PageView, PageDefaultProps, LangEnum, GlobalView } from '@utmb/types/index';

interface PageProps extends PageDefaultProps, PageView, RacePageView {}

const RaceRanking: NextPage<PageProps> = (props: PageProps): ReactElement => {
    const {
        logo,
        name,
        piCategory,
        results: resultsProp,
        hasCotation,
        host,
        isMobile,
        isCotationPending,
        isLogged,
        isPartialResults,
        distance,
        elevationGain,
    } = props;

    const { t } = useTranslation();
    const { locale, query } = useRouter();
    const { global } = useGlobal();
    const { tenantId } = useTenancy();
    const { isAuthenticated, getToken } = useUser();

    const { event, eventsTopBar, mobileBottomBar, navigation, simpleMenu, footer, partners, languages }: GlobalView = global;

    const [results, setResults] = useState<RaceResultPaginatedView>(resultsProp);

    const [isLoading, setIsLoading] = useState(false);
    const [showIndex, setShowIndex] = useState(isLogged);

    async function getResults(): Promise<void> {
        setIsLoading(true);

        const token = await getToken();

        let offset = resultsProp.offset;
        if (query.page) {
            const page = Number(query.page);
            if (page > 1) {
                offset = (page - 1) * resultsProp.limit;
            }
        }
        let filterGender = '';
        if (query.gender) {
            filterGender = query.gender.toString();
        }

        const results = (await fetchTheAPI({
            tenantId,
            isClient: true,
            path: `/races/${query.race}/results`,
            params: { lang: locale as string, offset: offset.toString(), gender: filterGender },
            bearer: token,
        })) as RaceResultPaginatedView;

        setShowIndex(results?.isLogged);
        setResults(results);
        setIsLoading(false);
    }

    useEffect(() => {
        if (isAuthenticated && !showIndex) {
            getResults().then();
        }
    }, [isAuthenticated, showIndex]);

    useEffect(() => {
        getResults().then();
    }, [query.page, query.gender]);

    const seoParams = {
        name,
        category: piCategory?.toUpperCase() ?? '-',
        finishers: resultsProp.nbHits,
        distance: `${distance} km`,
        elevationGain: `${elevationGain} mt+`,
    };

    const seo = {
        custom: [
            {
                description: t('raceRanking.raceSEO.description', seoParams),
                image: logo,
                title: t('raceRanking.raceSEO.title', seoParams),
                type: piCategory,
            },
        ],
        description: t('raceRanking.raceSEO.description', seoParams),
        image: logo,
        keywords: t('raceRanking.raceSEO.keywords', seoParams),
        title: t('raceRanking.raceSEO.title', seoParams),
    };

    const breadcrumbs = {
        lang: locale,
        slug: '/races/race',
        breadcrumbs: [
            {
                lang: locale,
                title: 'UTMB Index Races',
                slug: '/index-races',
            },
            {
                lang: locale,
                title: name,
                slug: null,
            },
        ],
    };

    const createYourAccountDatas = {
        title: t('raceRanking.createAccountBanner.title'),
        content: t('raceRanking.createAccountBanner.summary'),
    };

    return (
        <PageProviders>
            <Layout
                {...(seo as any)}
                host={host}
                contentLocale={locale}
                isMobile={isMobile}
                eventsTopBar={eventsTopBar}
                mobileBottomBar={mobileBottomBar}
                navigation={navigation}
                simpleMenu={simpleMenu}
                footer={footer}
                landing={false}
                event={event}
                partners={partners}
                languages={languages}
                breadcrumbs={breadcrumbs as any}
            >
                <RaceHeader {...props} />

                <RaceTable
                    isAuthenticated={showIndex}
                    results={results}
                    piCategory={piCategory}
                    hasCotation={hasCotation}
                    isCotationPending={isCotationPending}
                    isLoading={isLoading}
                    isPartialResults={isPartialResults}
                />

                <CreateYourAccount {...createYourAccountDatas} />
            </Layout>
        </PageProviders>
    );
};

export default RaceRanking;

export const getServerSideProps: GetServerSideProps<PageProps | any> = async ({ locale, defaultLocale, params, req, query }) => {
    try {
        // Translate
        const lang: LangEnum = (locale || defaultLocale || i18n.defaultLocale) as LangEnum;
        const translations = await serverSideTranslations(lang, ['common']);

        const host = req.headers.host;

        // Race Content
        const token = req.cookies[ACCESS_TOKEN_COOKIE_NAME];

        const raceParams: Record<string, string> = { lang };

        if (query.page) {
            raceParams.page = query.page as string;
        }

        const race = (await fetchTheAPI({
            req,
            path: `/races/${params?.race}`,
            params: raceParams,
            bearer: token,
        })) as RacePageView;

        // if error
        if (!race) return { notFound: true };

        const {
            date,
            distance,
            elevationGain,
            hasCotation,
            isCotationPending,
            isLogged,
            logo,
            name,
            piCategory,
            results,
            startCountry,
            startCountryCode,
            startPlace,
            url,
        } = race;

        return {
            props: {
                date,
                distance,
                elevationGain,
                hasCotation,
                host,
                isCotationPending,
                isLogged,
                isMobile: isMobile(req),
                logo,
                name,
                piCategory,
                results,
                startCountry,
                startCountryCode,
                startPlace,
                url,
                ...translations,
            },
        };
    } catch (error) {
        return {
            notFound: true,
        };
    }
};
